import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

const variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, position: 'relative' as const },
  // position: absolute takes the outgoing page out of document flow while it
  // fades, so it overlays in place instead of stacking above the incoming
  // page (which mounts and fades in at the same time — see App.tsx, no more
  // mode="wait" — for a true crossfade with no blank gap between them).
  exit: { opacity: 0, position: 'absolute' as const, top: 0, left: 0, right: 0 },
};

const transition = {
  duration: 0.32,
  ease: [0.22, 1, 0.36, 1] as const,
};

/**
 * Wrap any page root with <PageTransition> to get a smooth crossfade driven
 * by AnimatePresence in App.tsx: outgoing and incoming pages fade
 * simultaneously, so there's never a moment with neither on screen.
 *
 * Deliberately opacity-only otherwise (no y/transform, no filter, no
 * will-change: transform): a `transform`, `filter`, or `will-change:
 * transform` on this wrapper — even at its resting/zero value — creates a
 * new containing block for any `position: fixed` descendant. Every public
 * page renders through <Layout>, which has fixed elements (ScrollProgress,
 * FloatingContactWidget, ScrollToTop); those had stopped tracking the
 * viewport and instead pinned to this wrapper's box. Keep this transition to
 * properties that don't have that side effect.
 *
 * The `position: absolute` above relies on #root's `position: relative`
 * (see index.css) as its containing block.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  return (
    <motion.div
      className="w-full"
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={transition}
      style={{ willChange: 'opacity' }}
    >
      {children}
    </motion.div>
  );
}
